import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { Stars } from "@/components/Stars";
import { ADMIN } from "@/constants/testIds";
import { ReviewReply } from "@/components/AdminReviewReply";

const FILTERS = [["pending", "Waiting"], ["approved", "On the shop"], ["hidden", "Hidden"]];
const btn = "px-3 py-1.5 rounded-full text-xs font-medium transition-colors";

const ReviewCard = ({ r, onStatus, onDelete, onReply }) => (
  <li data-testid={ADMIN.reviewRow(r.id)} className="bg-white border border-[#F0EBE1] rounded-2xl p-4 flex gap-4">
    {r.photo ? <a href={imgUrl(r.photo)} target="_blank" rel="noreferrer"><img src={imgUrl(r.photo)} alt="Pet photo" className="w-20 h-20 rounded-xl object-cover shrink-0" /></a> : <div className="w-20 h-20 rounded-xl bg-[#F0EBE1] shrink-0" />}
    <div className="min-w-0 flex-1 text-sm">
      <div className="flex flex-wrap items-center gap-2"><Stars value={r.rating} size={14} /><span className="font-semibold">{r.product_name}</span></div>
      {r.text && <p className="mt-1 text-stone-700">{r.text}</p>}
      <p className="mt-1 text-xs text-stone-500">{[r.name, r.pet_name && `pet: ${r.pet_name}`, r.email, r.order_id].filter(Boolean).join(" · ")} · {new Date(r.created_at).toLocaleDateString()}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {r.status !== "approved" && <button data-testid={ADMIN.reviewApprove(r.id)} onClick={() => onStatus(r, "approved")} className={`${btn} bg-[#4E7B68] text-white hover:bg-[#3E6554]`}>Show on shop</button>}
        {r.status !== "hidden" && <button data-testid={ADMIN.reviewHide(r.id)} onClick={() => onStatus(r, "hidden")} className={`${btn} bg-[#F0EBE1] hover:bg-[#E7E0D3]`}>Hide</button>}
        <button data-testid={ADMIN.reviewDelete(r.id)} onClick={() => onDelete(r)} className={`${btn} text-red-700 hover:bg-red-50`}>Delete</button>
      </div>
      <ReviewReply r={r} onSaved={onReply} />
    </div>
  </li>
);

export const AdminReviews = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  useEffect(() => { api.get("/admin/reviews").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  const onStatus = async (r, status) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { status });
      setItems((l) => l.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Review is now on the product page" : "Review hidden");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (r) => {
    if (!window.confirm("Delete this review and its photo for good?")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      setItems((l) => l.filter((x) => x.id !== r.id));
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items) return <div className="mt-6 h-32 rounded-2xl bg-[#F0EBE1] animate-pulse" />;
  const shown = items.filter((r) => r.status === filter);
  return (
    <section className="mt-6">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([id, label]) => (
          <button key={id} data-testid={ADMIN.reviewFilter(id)} onClick={() => setFilter(id)} className={`${btn} ${filter === id ? "bg-[#1C1917] text-white" : "bg-white border border-[#E7E0D3] hover:bg-[#F0EBE1]"}`}>
            {label} ({items.filter((r) => r.status === id).length})
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-stone-500">Buyers get a review email 10 days after their order ships. New reviews wait here until you show them on the shop.</p>
      {shown.length
        ? <ul data-testid={ADMIN.reviewsList} className="mt-4 space-y-3">{shown.map((r) => <ReviewCard key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} onReply={(id, reply) => setItems((l) => l.map((x) => (x.id === id ? { ...x, reply } : x)))} />)}</ul>
        : <p data-testid={ADMIN.reviewsEmpty} className="mt-4 text-sm text-stone-500">No reviews here yet.</p>}
    </section>
  );
};
